"use server";

/**
 * Staff "Accept 1 revision" + shared freeze/blocked-period gates for request mutations
 * (William grill 2026-08-10).
 */

import { createClient } from '@/lib/supabase/server';
import {
  LEAD_TIME_FREEZE_COPY,
  findOverlappingBlockedPeriod,
  leadTimeStatus,
  type BlockedPeriod,
} from '@/lib/requestLeadTime';
import { revalidatePath } from 'next/cache';

const PATHS = ['/staff/requests', '/student/requests', '/student/cart', '/student/browse'];

/** Closed school / block-out periods — active `checkout_windows` rows (Admin
 * Academic Terms → Block-out Windows). Students may still request gear for dates
 * *outside* these ranges; overlapping a block-out is rejected by
 * assertRequestDatesNotBlocked (message names the block label when set). */
export async function getRequestBlockedPeriods(): Promise<BlockedPeriod[]> {
  const supabase = await createClient();
  const { data, error } = await supabase
    .from('checkout_windows')
    .select('label, opens_at, closes_at')
    .eq('active', true);
  if (error) throw new Error(error.message);

  return ((data ?? []) as { label: string; opens_at: string; closes_at: string }[]).map((w) => ({
    // Calendar dates in local campus sense — take the YYYY-MM-DD of each timestamp.
    startsOn: w.opens_at.slice(0, 10),
    endsOn: w.closes_at.slice(0, 10),
    label: w.label || undefined,
  }));
}

export async function assertRequestDatesNotBlocked(datesFrom: string | null, datesTo: string | null): Promise<void> {
  if (!datesFrom || !datesTo) return;
  const blocked = await getRequestBlockedPeriods();
  const hit = findOverlappingBlockedPeriod(datesFrom, datesTo, blocked);
  if (!hit) return;
  const name = hit.label?.trim();
  throw new Error(
    name
      ? `Those dates overlap “${name}”. Requests are blocked for that period — pick dates that do not leave gear out during ${name}.`
      : 'Those dates overlap a closed / block-out period. Pick dates that do not leave gear out during a blocked window.'
  );
}

/**
 * Blocks inventory-changing revisions inside the 3-business-day freeze unless staff
 * flipped accept_one_revision. Does not clear the flag — call
 * `consumeAcceptOneRevision` after a successful mutation.
 */
export async function assertRevisionAllowed(requestId: string): Promise<void> {
  const supabase = await createClient();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.from('requests') as any)
    .select('dates_from, accept_one_revision')
    .eq('id', requestId)
    .single();
  if (error) throw new Error(error.message);
  const checkout = data?.dates_from as string | null;
  if (!checkout) return;
  const { isFrozen } = leadTimeStatus(checkout);
  const accept = data?.accept_one_revision === true;
  if (isFrozen && !accept) {
    throw new Error(LEAD_TIME_FREEZE_COPY);
  }
}

/** One-shot: if the request was frozen and had accept_one_revision, clear it. */
export async function consumeAcceptOneRevision(requestId: string): Promise<void> {
  const supabase = await createClient();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.from('requests') as any)
    .select('dates_from, accept_one_revision')
    .eq('id', requestId)
    .single();
  if (error) throw new Error(error.message);
  if (!data?.accept_one_revision) return;
  const checkout = data.dates_from as string | null;
  if (!checkout || !leadTimeStatus(checkout).isFrozen) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: upErr } = await (supabase.from('requests') as any)
    .update({ accept_one_revision: false })
    .eq('id', requestId);
  if (upErr) throw new Error(upErr.message);
}

export async function setAcceptOneRevision(requestId: string, enabled: boolean): Promise<void> {
  const supabase = await createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: req, error: fetchErr } = await (supabase.from('requests') as any)
    .select('id, activity_log')
    .eq('id', requestId)
    .single();
  if (fetchErr) throw new Error(fetchErr.message);

  const log = (req.activity_log as { type: string; action: string; timestamp: string }[]) ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.from('requests') as any)
    .update({
      accept_one_revision: enabled,
      activity_log: [
        ...log,
        {
          type: 'accept_one_revision',
          action: enabled
            ? 'Staff allowed one revision inside the lead-time freeze'
            : 'Staff cleared Accept 1 revision',
          timestamp: new Date().toISOString(),
        },
      ],
    })
    .eq('id', requestId);
  if (error) throw new Error(error.message);
  PATHS.forEach((p) => revalidatePath(p));
}
